import { Alert, Box, Button, Link, TextField, Typography } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { type ChangeEvent, type FormEvent, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../../../../hooks/use-auth'
import { useP2PInviteLookup } from '../../../../hooks/use-p2pInviteLookup'
import { CreateInvitedAccount } from '../../../../services/peerToPeer/InvitationService'
import { AppRoutes } from '../../../Routes'
import { P2PAuthShell } from './P2PAuthShell'
import { PasswordRequirements } from './PasswordRequirements'
import { meetsPasswordRules } from './passwordRules'
import { styles } from './P2PCreateAccount.styles'

interface AccountForm {
  firstName: string
  lastName: string
  password: string
  confirmPassword: string
}

const EMPTY_FORM: AccountForm = { firstName: '', lastName: '', password: '', confirmPassword: '' }

/**
 * Creates the invitee's MiCredentials account from START. The email is the invited address and
 * can't be changed; START passes the password straight through to MiCredentials and keeps
 * nothing. A created account is signed straight in, and the landing then redeems the invitation.
 */
export const P2PCreateAccount = (): JSX.Element => {
  const auth = useAuth()
  const navigate = useNavigate()
  const invite = useP2PInviteLookup()
  const [form, setForm] = useState<AccountForm>(EMPTY_FORM)
  const [errors, setErrors] = useState<string[]>([])
  const [accountExists, setAccountExists] = useState<boolean>(false)
  const [invitationRejected, setInvitationRejected] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const goToStart = (): void => { navigate(AppRoutes.PEER_TO_PEER_START) }

  if (invite.status === 'loading') return <P2PAuthShell><></></P2PAuthShell>

  const onChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setForm({ ...form, [event.target.name]: event.target.value })
  }

  const passwordsMatch = form.password === form.confirmPassword
  const canSubmit = form.firstName.trim() !== '' && form.lastName.trim() !== '' &&
    meetsPasswordRules(form.password) && passwordsMatch && !isSubmitting

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault()
    if (invite.status !== 'valid' || !canSubmit) return

    setIsSubmitting(true)
    setErrors([])
    const { response, errors: requestErrors } = await CreateInvitedAccount({
      token: invite.token,
      firstName: form.firstName,
      lastName: form.lastName,
      password: form.password
    })
    const outcome = response?.data?.outcome
    if (outcome === 'Created') {
      await auth.setCurrentUser()
      goToStart()
      return
    }

    setIsSubmitting(false)
    if (outcome === 'AccountExists') {
      setAccountExists(true)
    } else if (outcome === 'InvalidInvitation') {
      setInvitationRejected(true)
    } else {
      setErrors(requestErrors.length > 0 ? requestErrors : ['Something went wrong creating your account. Please try again.'])
    }
  }

  if (invite.status === 'invalid' || invitationRejected) {
    return (
      <P2PAuthShell>
        <Box data-testid='p2pCreateAccount-invalid'>
          <Typography variant='h1' sx={styles.title}>Create Account</Typography>
          <Alert severity='warning' sx={styles.alert}>
            We couldn't find an open invitation. Open the link in your newest Peer to Peer invitation email to create an
            account, or ask your ISD administrator to resend it.
          </Alert>
          <BackToSignIn />
        </Box>
      </P2PAuthShell>
    )
  }

  if (accountExists) {
    return (
      <P2PAuthShell>
        <Box data-testid='p2pCreateAccount-exists'>
          <Typography variant='h1' sx={styles.title}>Create Account</Typography>
          <Alert severity='info' sx={styles.alert}>
            A MiCredentials account already exists for {invite.email}. Sign in with it instead. If you've forgotten the
            password, use "Forgot Password?" on the sign-in page.
          </Alert>
          <Button variant='contained' fullWidth endIcon={<ArrowForwardIcon />} onClick={goToStart} sx={styles.submit}>
            Sign in
          </Button>
          <BackToSignIn />
        </Box>
      </P2PAuthShell>
    )
  }

  return (
    <P2PAuthShell>
      <Box component='form' noValidate onSubmit={(event) => { void onSubmit(event) }} data-testid='p2pCreateAccount'>
        <Typography variant='h1' sx={styles.title}>Create Account</Typography>
        <Typography variant='body1' sx={styles.subtitle}>
          Create your MiCredentials account to start your Peer to Peer data collection.
        </Typography>

        {errors.length > 0 && (
          <Alert severity='error' sx={styles.alert} data-testid='p2pCreateAccount-errors'>
            {errors.map(error => <div key={error}>{error}</div>)}
          </Alert>
        )}

        <Box sx={styles.section}>
          <Typography variant='h4' sx={styles.sectionHeading}>Background information</Typography>
          <Typography variant='body1'>Provide some background information about yourself</Typography>
          <TextField variant='outlined' label='First Name' name='firstName' placeholder='Enter first name'
            value={form.firstName} onChange={onChange} fullWidth required
          />
          <TextField variant='outlined' label='Last Name' name='lastName' placeholder='Enter last name'
            value={form.lastName} onChange={onChange} fullWidth required
          />
        </Box>

        <Box sx={styles.section}>
          <Typography variant='h4' sx={styles.sectionHeading}>Account details</Typography>
          <TextField variant='outlined' label='Email' value={invite.email} fullWidth disabled
            helperText='This is the address your invitation was sent to.'
          />
          <TextField variant='outlined' label='Password' name='password' type='password' autoComplete='new-password'
            value={form.password} onChange={onChange} fullWidth required
          />
          <PasswordRequirements password={form.password} />
          <TextField variant='outlined' label='Confirm Password' name='confirmPassword' type='password'
            autoComplete='new-password' value={form.confirmPassword} onChange={onChange} fullWidth required
            error={form.confirmPassword !== '' && !passwordsMatch}
            helperText={form.confirmPassword !== '' && !passwordsMatch ? 'Passwords do not match' : ' '}
          />
        </Box>

        <Button type='submit' variant='contained' color='primary' fullWidth endIcon={<ArrowForwardIcon />}
          disabled={!canSubmit} sx={styles.submit} data-testid='p2pCreateAccount-submit'
        >
          Create Account
        </Button>

        <BackToSignIn />
      </Box>
    </P2PAuthShell>
  )
}

const BackToSignIn = (): JSX.Element => (
  <Link component={RouterLink} to={AppRoutes.PEER_TO_PEER_START} sx={styles.backLink}>
    Back to sign-in options
  </Link>
)
